import { ExternalLink, PersonStanding } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BUILD_ID } from '../buildId';
import { BrandLogo } from './BrandLogo';
import {
  areaOrigin,
  grafanaOrigin,
  isOnPublicOrigin,
  publicUrl,
} from '../helpers/areaLinks';
import { useKeyBurst } from '../hooks/useKeyBurst';
import { useT } from '../i18n/I18nProvider';

/** Operator tools, shown with the hub and admin links. Brand names, so not translated. */
const EXTERNAL_TOOLS = [
  { name: 'Hetzner', href: 'https://console.hetzner.com/' },
  {
    name: 'Namecheap',
    href: 'https://ap.www.namecheap.com/Domains/DomainControlPanel/pixelperfect.casa/domain/',
  },
  {
    name: 'Doppler',
    href: 'https://dashboard.doppler.com/workplace/609f184d812388fbd020/projects/takove/configs/prd',
  },
  { name: 'SendPulse', href: 'https://login.sendpulse.com/smtp/' },
  { name: 'Zoho Mail Admin', href: 'https://mailadmin.zoho.com/' },
  {
    name: 'Zoho Mail',
    href: 'https://workplace.zoho.com/#mail_app/mail/folder/inbox',
  },
  { name: 'Lucide', href: 'https://lucide.dev/icons/' },
];

/**
 * Footer for every layout (after prototype@c0c19a1 `app/public-home.tsx`):
 * the brand and tagline, the accessibility statement link (symbol plus
 * visible text, never icon-only) and the deployed build's short git SHA as
 * plain text. The statement lives on the apex only, so from another area's
 * host the link is an absolute URL to it (ADR 0016 §7). The hub and admin
 * links, Grafana and the operator tools stay hidden until Shift is pressed
 * 5 times within 5 seconds.
 */
export function AppFooter() {
  const { t } = useT();
  const linkClass = 'link inline-flex items-center gap-1.5';
  const icon = <PersonStanding aria-hidden="true" className="h-4 w-4" />;
  const label = t('app.footer:accessibility');
  const showAreaLinks = useKeyBurst('Shift', 5, 5000);
  const areaLinks = (['hub', 'admin'] as const).flatMap((area) => {
    const origin = areaOrigin(area);
    return origin ? [{ area, origin }] : [];
  });
  const grafana = grafanaOrigin();
  const externalLinks = [
    ...(grafana ? [{ name: 'Grafana', href: grafana }] : []),
    ...EXTERNAL_TOOLS,
  ];

  return (
    <footer className="border-t border-base-300 bg-base-100 text-sm">
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-6">
        <div className="flex flex-col gap-1">
          <BrandLogo />
          <p>{t('app.footer:tagline')}</p>
        </div>
        <nav
          aria-label={t('app.footer:navigation')}
          className="flex flex-wrap items-center gap-x-4 gap-y-1"
        >
          {isOnPublicOrigin() ? (
            <Link to="/accessibility" className={linkClass}>
              {icon}
              {label}
            </Link>
          ) : (
            <a href={publicUrl('/accessibility')} className={linkClass}>
              {icon}
              {label}
            </a>
          )}
          {showAreaLinks && (
            <div
              data-testid="secret-links"
              className="flex flex-wrap items-center gap-x-4 gap-y-1 opacity-40"
            >
              {areaLinks.map(({ area, origin }) => (
                <a key={area} href={origin} className="link">
                  {t(`app.footer:${area}`)}
                </a>
              ))}
              {externalLinks.map(({ name, href }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {name}
                  <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          )}
        </nav>
        <span>{t('app.footer:build', { id: BUILD_ID })}</span>
      </div>
    </footer>
  );
}
